import { translate } from '@teamleader/i18n';
import { IconBlockMediumOutline, IconDownloadMediumOutline, IconMailMediumOutline } from '@teamleader/ui-icons';
import fileDownload from 'js-file-download';
import React, { useCallback, useContext, useMemo } from 'react';

import QuotationName from './QuotationName';
import { useIsBase, useIsMedium, useIsSmall } from '../../../../Components/Context/Hooks/mediaQueries';
import Box, { BoxProps } from '../../../../Components/UI/Box';
import Button from '../../../../Components/UI/Button';
import IconButton from '../../../../Components/UI/IconButton';
import { WithTooltip } from '../../../../Components/UI/Tooltip';
import { AccountSettingOption } from '../../../../Types/cloudPlatform';
import { CUSTOMER_PORTAL_STATUS, QUOTATION_STATUS } from '../../../../Types/quotation';
import { MENUS } from '../../../../Utils/Menus';
import { EVENTS, track } from '../../../../Utils/analytics';
import { formatDateShort, fromISO } from '../../../../Utils/date';
import { MODES, UrlContext } from '../../../../Utils/jwt';
import useGlobalContext, { DealContext } from '../../Context';
import QuotationStatusLabel from '../../Navigation/Deals/QuotationStatusLabel';
import Header from '../Header';
import styles from '../styles.css';

const ButtonWithTooltip = WithTooltip(Button);
const IconButtonWithTooltip = WithTooltip(IconButton);

const DealsHeader: React.FC<BoxProps> = ({ ...rest }) => {
  const { deal, quotation, pdf } = useContext(DealContext);
  const { handleOpenMenu, request, isAccountSettingEnabled } = useGlobalContext();
  const { mode } = useContext(UrlContext);
  const isBase = useIsBase();
  const isSmall = useIsSmall();
  const isMedium = useIsMedium();

  const isExpired = quotation?.status === QUOTATION_STATUS.EXPIRED;
  const isRefused = quotation?.status === QUOTATION_STATUS.REFUSED;
  const isAccepted = quotation?.status === QUOTATION_STATUS.ACCEPTED;
  const lockedAfterExpiry = quotation?.expiry.lockedAfterExpiry;
  const isAlreadySigned = useMemo(
    () => deal?.quotations.some((quotation) => quotation.customerPortalStatus === CUSTOMER_PORTAL_STATUS.SIGNED),
    [deal],
  );
  const shouldDisableSign = !pdf || isAlreadySigned || (isExpired && lockedAfterExpiry) || isRefused;
  const shouldDisableSendFeedback = !pdf;
  const canRefuseQuotation = !!isAccountSettingEnabled?.(AccountSettingOption.RefuseQuotationsFromCustomerPortal);
  const shouldDisableRefuse = isRefused || isAccepted || isExpired || !pdf;

  const feedbackVisible = !!deal?.responsibleUser?.email || !!deal?.department?.email;

  const handleDownload = useCallback(() => {
    (async () => {
      const data = await pdf!.getData();
      fileDownload(data, `${quotation!.name}.pdf`, 'application/pdf');
      track(EVENTS.DOWNLOADED, { quotation: quotation!.id });

      if (quotation?.customerPortalStatus !== CUSTOMER_PORTAL_STATUS.SIGNED) {
        await request!('/deals.markAsDownloaded', {
          body: JSON.stringify({
            id: quotation!.id,
          }),
        });
      }
    })();
  }, [pdf, quotation, request]);

  const handleSign = useCallback(() => {
    handleOpenMenu!(MENUS.SIGN);
    track(EVENTS.STARTED_SIGNING, { fromHeader: true });
  }, [handleOpenMenu]);

  const handleSendFeedback = useCallback(() => {
    handleOpenMenu!(MENUS.FEEDBACK);
    track(EVENTS.STARTED_ADDING_FEEDBACK);
  }, [handleOpenMenu]);

  const handleRefuse = useCallback(() => {
    handleOpenMenu!(MENUS.REFUSE);
    track(EVENTS.STARTED_ADDING_FEEDBACK);
  }, [handleOpenMenu]);

  return (
    <Header {...rest}>
      {quotation && (
        <>
          <Box className={styles['title-wrapper']}>
            <QuotationName quotation={quotation} />
            {!isSmall && quotation.customerPortalStatus === CUSTOMER_PORTAL_STATUS.SIGNED && (
              <QuotationStatusLabel status={quotation.customerPortalStatus} marginLeft={3} />
            )}
          </Box>
          <Box display="flex" alignItems="center" marginLeft={3}>
            {quotation.createdAt && isMedium && (
              <Box className={styles['data']}>
                <h4 className={styles['data-label']}>{translate('lastUpdated')}</h4>
                <span className={styles['data-value']}>
                  {quotation.updatedAt
                    ? formatDateShort(fromISO(quotation.updatedAt))
                    : formatDateShort(fromISO(quotation.createdAt))}
                </span>
              </Box>
            )}
            <Box marginLeft={3}>
              <IconButtonWithTooltip
                icon={<IconDownloadMediumOutline />}
                onClick={handleDownload}
                title={translate('actions.download')}
                disabled={!pdf}
                label={mode === MODES.PREVIEW ? translate('preview.actions') : undefined}
              />
            </Box>
            {isBase && canRefuseQuotation && (
              <IconButton
                marginLeft={3}
                icon={<IconBlockMediumOutline />}
                onClick={handleRefuse}
                title={translate('actions.refuse')}
                disabled={shouldDisableRefuse}
              />
            )}
            {!isBase && canRefuseQuotation && (
              <Button onClick={handleRefuse} disabled={shouldDisableRefuse} marginLeft={3} level="destructive">
                {translate('actions.refuse')}
              </Button>
            )}
            {isBase && feedbackVisible && (
              <IconButton
                marginLeft={3}
                icon={<IconMailMediumOutline />}
                onClick={handleSendFeedback}
                title={translate('actions.sendEmail')}
                disabled={shouldDisableSendFeedback}
              />
            )}
            {!isBase && feedbackVisible && (
              <Button onClick={handleSendFeedback} disabled={shouldDisableSendFeedback} marginLeft={3}>
                {translate('actions.sendEmail')}
              </Button>
            )}
            {!isBase && (
              <Box marginLeft={3}>
                <ButtonWithTooltip
                  onClick={handleSign}
                  disabled={shouldDisableSign}
                  level="primary"
                  label={
                    isAlreadySigned
                      ? quotation.customerPortalStatus === CUSTOMER_PORTAL_STATUS.SIGNED
                        ? translate('tooltips.alreadySigned')
                        : translate('tooltips.maxQuotationsSigned')
                      : undefined
                  }
                  placement="left"
                >
                  {translate('actions.signQuotation')}
                </ButtonWithTooltip>
              </Box>
            )}
          </Box>
        </>
      )}
    </Header>
  );
};

export default DealsHeader;
